import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import type { CustomerGroup, CustomerGroupInput } from '@wa/shared';
import { Card, Field, Notice, Switch, danger, errorText, input, primary, secondary } from './ui';

const METHODS: { key: string; label: string }[] = [
  { key: 'online', label: 'Pay online' },
  { key: 'cod', label: 'Cash on delivery' },
  { key: 'on_account', label: 'On account (approved customers)' },
];

/**
 * Customer types. The default is where every contact without a type lives, so
 * there is always one; deleting a type moves its contacts rather than orphaning
 * them.
 */
export function GroupsPanel() {
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ['shop', 'groups'], queryFn: shopApi.groups });
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CustomerGroup | 'new' | null>(null);

  const refresh = () => { setError(null); setEditing(null); void queryClient.invalidateQueries({ queryKey: ['shop'] }); };
  const onError = (e: unknown) => setError(errorText(e, 'Could not save the customer type.'));

  const create = useMutation({ mutationFn: (i: CustomerGroupInput) => shopApi.createGroup(i), onSuccess: refresh, onError });
  const update = useMutation({ mutationFn: ({ id, i }: { id: number; i: CustomerGroupInput }) => shopApi.updateGroup(id, i), onSuccess: refresh, onError });
  const setDefault = useMutation({ mutationFn: (id: number) => shopApi.setDefaultGroup(id), onSuccess: refresh, onError });
  const remove = useMutation({ mutationFn: (id: number) => shopApi.deleteGroup(id), onSuccess: refresh, onError });

  return (
    <div className="flex flex-col gap-4">
      {error && <Notice kind="bad">{error}</Notice>}

      <Card title="Customer types" blurb="Retail, dealer, distributor — each sees its own prices, its own products and its own payment options. New customers land in the default."
            actions={<button type="button" className={primary} onClick={() => setEditing('new')}>Add a type</button>}>
        {groups.isLoading && <Notice kind="muted">Loading…</Notice>}
        {groups.data && groups.data.length === 0 && <Notice kind="muted">No customer types yet — one called Retail is created when the shop is saved.</Notice>}
        <ul className="divide-y divide-(--color-line-dim)">
          {groups.data?.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <div className="min-w-0 flex-1 basis-48">
                <p className="text-small font-medium">
                  {g.name}
                  {g.isDefault && <span className="ml-2 rounded bg-(--color-sunken) px-1.5 py-0.5 text-micro text-(--color-muted)">default</span>}
                </p>
                <p className="text-micro text-(--color-muted)">
                  {g.contacts.toLocaleString('en-IN')} customers · pays by {g.allowedPayMethods.split(',').map((m) => METHODS.find((x) => x.key === m)?.label.toLowerCase() ?? m).join(', ')}
                  {g.minOrderValue ? ` · min order ₹${g.minOrderValue.toLocaleString('en-IN')}` : ''}
                  {g.b2bInvoice ? ' · B2B invoice' : ''}
                </p>
              </div>
              <button type="button" className={secondary} onClick={() => setEditing(g)}>Edit</button>
              {!g.isDefault && <button type="button" className={secondary} disabled={setDefault.isPending} onClick={() => setDefault.mutate(g.id)}>Make default</button>}
              {!g.isDefault && (
                <button type="button" className={danger} disabled={remove.isPending}
                        onClick={() => { if (window.confirm(`Delete "${g.name}"? Its ${g.contacts} customers move to the default type.`)) remove.mutate(g.id); }}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {editing && (
        <GroupEditor
          group={editing === 'new' ? null : editing}
          busy={create.isPending || update.isPending}
          onCancel={() => setEditing(null)}
          onSave={(i) => (editing === 'new' ? create.mutate(i) : update.mutate({ id: editing.id, i }))}
        />
      )}
    </div>
  );
}

function GroupEditor({ group, busy, onCancel, onSave }: {
  group: CustomerGroup | null; busy: boolean; onCancel: () => void; onSave: (i: CustomerGroupInput) => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [minOrder, setMinOrder] = useState(group?.minOrderValue ? String(group.minOrderValue) : '');
  const [methods, setMethods] = useState<string[]>(group?.allowedPayMethods.split(',') ?? ['online', 'cod']);
  const [b2b, setB2b] = useState(group?.b2bInvoice ?? false);

  const toggle = (key: string) => setMethods((m) => (m.includes(key) ? m.filter((x) => x !== key) : [...m, key]));

  return (
    <Card title={group ? `Edit ${group.name}` : 'New customer type'}>
      <div className="grid gap-3 @min-[32rem]:grid-cols-2">
        <Field label="Name"><input className={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Dealer" /></Field>
        <Field label="Minimum order (₹)" hint="Leave empty for none.">
          <input className={input} type="number" min={0} value={minOrder} onChange={(e) => setMinOrder(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Payment methods</span>
        {METHODS.map((m) => <Switch key={m.key} checked={methods.includes(m.key)} onChange={() => toggle(m.key)} label={m.label} />)}
        <Switch checked={b2b} onChange={setB2b} label="B2B invoice" hint="Ask for the customer's GSTIN at checkout and print it on the invoice." />
      </div>
      <div className="flex gap-2">
        <button type="button" className={primary} disabled={busy || !name.trim() || methods.length === 0}
                onClick={() => onSave({ name: name.trim(), minOrderValue: minOrder === '' ? 0 : Number(minOrder), allowedPayMethods: methods.join(','), b2bInvoice: b2b })}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={secondary} onClick={onCancel}>Cancel</button>
      </div>
    </Card>
  );
}
